import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DataTable,
  type ColumnDef,
  type SortState,
  type PaginationState,
} from '@/components/data-table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { FormInput, FormSelect, FormTextarea } from '@/components/forms/FormField';
import {
  useDebitNotesList,
  useCreateDebitNote,
  type DebitNote,
  type DebitNotesListParams,
  type DebitNoteStatus,
  type CreateDebitNotePayload,
} from './hooks/useBilling';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useToast } from '@/components/ui/Toast';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_BADGE_MAP: Record<
  DebitNoteStatus,
  { variant: 'default' | 'info' | 'warning' | 'success' | 'danger' | 'outline'; label: string }
> = {
  draft: { variant: 'default', label: 'Draft' },
  issued: { variant: 'success', label: 'Issued' },
};

// ─── Validation Schema ────────────────────────────────────────────────────────

const debitNoteSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  amount: z.coerce
    .number()
    .min(0.01, 'Amount must be greater than 0')
    .max(9999999.99, 'Amount must be less than 10,000,000'),
  reason: z
    .string()
    .min(1, 'Reason is required')
    .max(2000, 'Reason must be 2000 characters or less'),
  status: z.enum(['draft', 'issued']),
});

type DebitNoteFormValues = z.infer<typeof debitNoteSchema>;

/**
 * DebitNotesPage — debit notes listing with create modal.
 * Requirements: 15.19
 */
export function DebitNotesPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { toast } = useToast();

  // ─── List State ─────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<DebitNoteStatus | ''>('');
  const [sort, setSort] = useState<SortState>({ column: 'createdAt', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const params: DebitNotesListParams = useMemo(
    () => ({
      page,
      pageSize,
      search: search || undefined,
      status: status || undefined,
      sortBy: sort.direction ? sort.column : undefined,
      sortDirection: sort.direction ?? undefined,
    }),
    [page, pageSize, search, status, sort],
  );

  const { data, isLoading } = useDebitNotesList(params);

  // ─── Table Columns ──────────────────────────────────────────────────────────
  const columns: ColumnDef<DebitNote>[] = useMemo(
    () => [
      {
        id: 'clientName',
        header: 'Client',
        accessorKey: 'clientName',
        sortable: true,
      },
      {
        id: 'amount',
        header: 'Amount',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span className="font-medium text-gray-900 dark:text-gray-100">
            ${row.amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        id: 'reason',
        header: 'Reason',
        cell: (row) => (
          <span className="max-w-xs truncate text-gray-700 dark:text-gray-300" title={row.reason}>
            {row.reason}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        cell: (row) => {
          const badge = STATUS_BADGE_MAP[row.status];
          return (
            <Badge variant={badge.variant} size="sm">
              {badge.label}
            </Badge>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        sortable: true,
        cell: (row) => (
          <span className="text-gray-700 dark:text-gray-300">
            {new Date(row.createdAt).toLocaleDateString('en-CA')}
          </span>
        ),
      },
    ],
    [],
  );

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const pagination: PaginationState | undefined = data
    ? { page: data.page, pageSize: data.pageSize, total: data.total }
    : undefined;

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);
  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    toast({ variant: 'success', title: 'Debit note created successfully' });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Debit Notes</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create and manage debit notes for clients
          </p>
        </div>
        {can(PERMISSIONS.BILLING_CREATE_INVOICE) && (
          <Button onClick={() => navigate('/billing/new?type=debit_note')}>
            Create Debit Note
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-48">
            <Select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as DebitNoteStatus | '');
                setPage(1);
              }}
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'issued', label: 'Issued' },
              ]}
              placeholder="All statuses"
            />
          </div>
          {status && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatus('');
                setPage(1);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {/* Data Table */}
      <DataTable<DebitNote>
        data={data?.debitNotes ?? []}
        columns={columns}
        keyExtractor={(row) => row.id}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={isLoading}
        emptyMessage="No debit notes found. Create your first debit note to get started."
        filterValue={search}
        onFilterChange={handleSearchChange}
        filterPlaceholder="Search by client name, reason..."
      />

      {/* Create Debit Note Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Debit Note"
        size="md"
      >
        <DebitNoteCreateForm
          onSuccess={handleCreateSuccess}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>
    </div>
  );
}

// ─── Debit Note Create Form (Modal) ────────────────────────────────────────

import { useContactsList } from '@/features/contacts/hooks/useContacts';

interface DebitNoteCreateFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

function DebitNoteCreateForm({ onSuccess, onCancel }: DebitNoteCreateFormProps) {
  const { toast } = useToast();
  const createMutation = useCreateDebitNote();

  // Fetch clients for dropdown
  const { data: clientsData } = useContactsList({ contactTypeNature: 'client', pageSize: 200 });
  const clientOptions = (clientsData?.contacts ?? []).map((c) => ({
    value: c.id,
    label: `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email || c.id,
  }));

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<DebitNoteFormValues>({
    resolver: zodResolver(debitNoteSchema),
    defaultValues: {
      clientId: '',
      amount: 0,
      reason: '',
      status: 'draft',
    },
  });

  const onSubmit = async (values: DebitNoteFormValues) => {
    const payload: CreateDebitNotePayload = {
      clientId: values.clientId,
      amount: values.amount,
      reason: values.reason,
      status: values.status,
    };

    try {
      await createMutation.mutateAsync(payload);
      onSuccess();
    } catch {
      toast({ variant: 'error', title: 'Failed to create debit note' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <FormSelect
        name="clientId"
        control={control}
        label="Client"
        required
        options={clientOptions}
        placeholder="Select a client"
      />

      <FormInput
        name="amount"
        control={control}
        label="Amount"
        type="number"
        min={0.01}
        step="0.01"
        required
        placeholder="0.00"
      />

      <FormTextarea
        name="reason"
        control={control}
        label="Reason"
        required
        placeholder="Reason for debit note..."
        rows={3}
      />

      <FormSelect
        name="status"
        control={control}
        label="Status"
        required
        options={[
          { value: 'draft', label: 'Draft' },
          { value: 'issued', label: 'Issued' },
        ]}
      />

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={isSubmitting}>
          Create Debit Note
        </Button>
      </div>
    </form>
  );
}
